/**
 * Shared cache for subscriber-routes trial info.
 * All course pages (CourseOverview, TabsPage, LessonsList) need the same
 * { userFreeRoutes, maxFreeRoutes, routeStart } data from the same API.
 * This module fetches it once and caches for 60 seconds to avoid
 * hammering the endpoint on every page transition.
 */
import config from "../settings/config";
import { getAuthToken } from "./authStorage";

let cachedResult = null;
let cacheTimestamp = 0;
let inflightPromise = null;
const CACHE_TTL_MS = 60_000; // 60 seconds

const isRouteEligibleForFreeStart = (row) =>
  row?.route?.free === true || row?.route?.is_default_for_free === true;

/**
 * Fetch trial info, returning cached data if fresh enough.
 * Deduplicates in-flight requests so parallel callers share one fetch.
 * @returns {Promise<{userFreeRoutes: number, maxFreeRoutes: number, routeStart: string|null} | null>}
 */
export async function getTrialInfo() {
  const now = Date.now();

  // Return cache if still fresh
  if (cachedResult && now - cacheTimestamp < CACHE_TTL_MS) {
    return cachedResult;
  }

  // If a request is already in flight, piggyback on it
  if (inflightPromise) {
    return inflightPromise;
  }

  inflightPromise = (async () => {
    try {
      const res = await fetch(
        `${
          config.env[config.env.mode].url
        }/api/master-club-route/subscriber-routes`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
        },
      );
      if (!res.ok) return cachedResult; // keep stale data on error

      const data = await res.json();
      const freeRoute = (Array.isArray(data) ? data : []).find(
        isRouteEligibleForFreeStart,
      );

      if (freeRoute) {
        // Normalize routeStart to "YYYY-MM-DD" — the API may return a full
        // ISO timestamp like "2026-03-16T00:00:00.000Z" but downstream
        // helpers (computeDayISO, getActiveWeekdayISO) expect a plain date.
        let rawStart = freeRoute.route?.start ?? null;
        if (rawStart && rawStart.includes("T")) {
          rawStart = rawStart.split("T")[0];
        }
        cachedResult = {
          userFreeRoutes: freeRoute.user_free_routes,
          maxFreeRoutes: freeRoute.max_free_routes,
          routeStart: rawStart,
        };
      } else {
        cachedResult = null;
      }
      cacheTimestamp = Date.now();
      return cachedResult;
    } catch {
      return cachedResult; // keep stale data on error
    } finally {
      inflightPromise = null;
    }
  })();

  return inflightPromise;
}

/** Force-clear the cache (e.g. after starting/cancelling a trial). */
export function invalidateTrialInfoCache() {
  cachedResult = null;
  cacheTimestamp = 0;
}
